import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';

import type { StorageE } from 'declarations/enums/storage';

import {
  CollapsibleCardCollapsedStyled,
  CollapsibleCardContentStyled,
  CollapsibleCardHeaderStyled,
  CollapsibleCardWrapStyled,
  CollapsibleChevronColumnStyled,
} from './HorizontallyCollapsibleCard.styled';

type HorizontallyCollapsibleCardPropsT = {
  children: ReactNode;
  title: string;
  storageKey: StorageE;
  collapsedIcon: ReactNode;
  collapsedCount?: number;
  disableOnMobile?: boolean;
  isMobile?: boolean;
};

export const HorizontallyCollapsibleCard: FC<HorizontallyCollapsibleCardPropsT> = ({
  children,
  title,
  storageKey,
  collapsedIcon,
  collapsedCount,
  disableOnMobile = true,
  isMobile = false,
}): JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState((): boolean => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === 'true';
    } catch {
      return false;
    }
  });

  useEffect((): void => {
    try {
      localStorage.setItem(storageKey, String(isCollapsed));
    } catch {
      // storage not available
    }
  }, [isCollapsed, storageKey]);

  const showCollapse = !disableOnMobile || !isMobile;

  if (!showCollapse) {
    return <>{children}</>;
  }

  const handleToggle = (): void => {
    setIsCollapsed((prev): boolean => !prev);
  };

  const chevronButton = (
    <Tooltip title={isCollapsed ? 'Expand' : 'Collapse'} arrow>
      <IconButton
        aria-label={isCollapsed ? 'Expand' : 'Collapse'}
        onClick={handleToggle}
        sx={{ color: (t): string => t.palette.primary.main }}
      >
        {isCollapsed ? (
          <ChevronRightIcon sx={{ fontSize: 24 }} />
        ) : (
          <ChevronLeftIcon sx={{ fontSize: 24 }} />
        )}
      </IconButton>
    </Tooltip>
  );

  return (
    <CollapsibleCardWrapStyled $collapsed={isCollapsed}>
      {isCollapsed ? (
        <>
          <CollapsibleCardHeaderStyled>{chevronButton}</CollapsibleCardHeaderStyled>
          <CollapsibleCardCollapsedStyled
            onClick={handleToggle}
            onKeyDown={(e): void => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
            }}
            role="button"
            tabIndex={0}
            aria-label={`Expand ${title}`}
          >
            <Tooltip
              title={`${title}${collapsedCount !== undefined ? ` (${collapsedCount})` : ''}`}
              arrow
              placement="right"
            >
              <Box
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}
              >
                <Box
                  sx={{
                    color: (t): string => t.palette.primary.main,
                    '& .MuiSvgIcon-root': { fontSize: 28 },
                  }}
                >
                  {collapsedIcon}
                </Box>
                {collapsedCount !== undefined && (
                  <Typography
                    component="span"
                    sx={{ fontSize: '14px', fontWeight: 600, color: 'success.main' }}
                  >
                    ({collapsedCount})
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </CollapsibleCardCollapsedStyled>
        </>
      ) : (
        <>
          <CollapsibleChevronColumnStyled>{chevronButton}</CollapsibleChevronColumnStyled>
          <CollapsibleCardContentStyled>{children}</CollapsibleCardContentStyled>
        </>
      )}
    </CollapsibleCardWrapStyled>
  );
};
